import * as React from 'react';
import {
    GIFormControl,
    GICheckbox,
    GIFormLabel,
    GIInput,
    InputSize,
} from '@components/common/app-components/GIInput';
import GIText from '@components/common/app-components/GIText/GIText';
import GITextIntl from '@components/common/app-components/GIText/GITextIntl';
import Table from '@components/common/app-components/Table';
import type { IHardshipExemptionEligibilityTableProps } from './types/apiResponse';
import { VALIDATION_TYPES } from '@constants/inputs/validationTypes';
import Tooltip from '@components/common/tooltip/Tooltip';
import styled from 'styled-components';
import { DIGIT_ONLY_REGEX } from '@utils/validators/regex';
import { useIntl } from 'react-intl';
const StyledWrapper = styled.div`
    .gi-input {
        &::-webkit-outer-spin-button,
        &::-webkit-inner-spin-button {
            -webkit-appearance: none;
            margin: 0;
        }

        /* Remove Firefox spin buttons */
        &[type='number'] {
            -moz-appearance: textfield;
        }
    }
`;

/**
 * Component for rendering the hardship exemption eligibility table.
 *
 * @param {IHardshipExemptionEligibilityTableProps} props - The props for the component.
 * @returns {React.ReactNode | null} - The rendered table component or null if no data is available.
 */

const HardshipExemptionEligibilityTable: React.FC<IHardshipExemptionEligibilityTableProps> = ({
    additionalInfo,
    data,
    isHardExemptionChecked,
    onHandleExemptionNumberChange,
    onHandleTobaccoUseChange,
    onHandleHasDisabilityChange,
    showSeekingCoverage,
    shouldShowSeekingCoverageTooltip,
    shouldShowHouseholdMembers,
    shouldShowTobaccoUseCheckRequired,
}) => {
    const { showTobaccoCheckBox, showBlindOrDisabilityCheckBox } = additionalInfo;
    const intl = useIntl();
    const colHeader = [
        {
            titleId: 'indportal.tobacco.table.householdMembers.heading',
            key: 'name',
            tooltipId: shouldShowHouseholdMembers ? 'indportal.tobacco.householdMembersPopup' : undefined,
            render: item => {
                return (
                    <GIText id={`name-${item.personId}`} data-testid={`name-${item.personId}`}>
                        {item.name}
                    </GIText>
                );
            },
        },
        ...(showSeekingCoverage
            ? [
                {
                    titleId: 'indportal.tobacco.table.seekingCoverage.heading',
                    key: 'applyingForCoverage',
                    tooltipId: shouldShowSeekingCoverageTooltip ? 'indportal.tobacco.seekingCoveragePopUp' : undefined,
                    contentReplacement: 'indportal.tobacco.table.seekingCoverage.heading',
                    render: item => (
                        <GIFormControl
                            id={`applyingForCoverageWrapper-${item.personId}`}
                            name={`applyingForCoverage-${item.personId}`}
                            type="checkbox"
                        >
                            <GICheckbox
                                id={`applyingForCoverage-${item.personId}`}
                                checked={item.applyingForCoverage}
                                disabled
                            />
                            <GIFormLabel
                                id={`applyingForCoverageLabel-${item.personId}`}
                                name={`applyingForCoverage-${item.personId}`}
                                aria-label={`${item.name} ${intl.formatMessage({ id: 'indportal.tobacco.table.seekingCoverage.heading' })}`}
                            ></GIFormLabel>
                        </GIFormControl>
                    ),
                },
            ]
            : []),
        ...(showTobaccoCheckBox
            ? [
                {
                    // checkbox should be disabled if item.isChildDependant is true and item.age is < 21
                    // Don't show this field if item.isChildDependant is false, instead display text 'N/A'
                    titleId: 'indportal.tobacco.table.tobaccoUse.heading',
                    key: 'enableTobacco',
                    tooltipId: 'indportal.tobacco.tobaccoUsePopUp',
                    inputRequired: shouldShowTobaccoUseCheckRequired,
                    render: item => {
                        if (item.age < VALIDATION_TYPES.TOBACCO_AGE_LIMIT) {
                            return (
                                <Tooltip
                                    id="indportal.tobacco.table.data.na"
                                    index="tobaccoUse_na_tooltip"
                                    content="indportal.tobacco.kidTooltip"
                                    triggerDecorationClassName="border-none"
                                />
                            );
                        }
                        return (
                            <GIFormControl
                                id={`tobaccoUserWrapper-${item.personId}`}
                                name={`tobaccoUser-${item.personId}`}
                                type="checkbox"
                            >
                                <GICheckbox
                                    id={`tobaccoUser-${item.personId}`}
                                    checked={item.tobaccoUser}
                                    value={item.tobaccoUser}
                                    disabled={!item.enableTobacco}
                                    onChange={e => onHandleTobaccoUseChange(item.personId, !item.tobaccoUser)}
                                />
                                <GIFormLabel
                                    id={`tobaccoUserLabel-${item.personId}`}
                                    name={`tobaccoUser-${item.personId}`}
                                    aria-label={`${item.name} ${intl.formatMessage({ id: 'indportal.tobacco.table.tobaccoUse.heading' })}`}
                                />
                            </GIFormControl>
                        );
                    },
                },
            ]
            : []),
        ...(showBlindOrDisabilityCheckBox
            ? [
                {
                    titleId: 'indportal.tobacco.table.hasDisability.heading',
                    key: 'blindOrDisabled',
                    render: item => {
                        // If not a child dependant, display 'N/A'
                        if (!item.isChildDependant) {
                            return (
                                <GITextIntl
                                    messageId="indportal.tobacco.table.data.na"
                                    id={'blindOrDisabled__data--na'}
                                />
                            );
                        }
                        return (
                            <GIFormControl
                                id={`blindOrDisabledWrapper-${item.personId}`}
                                name={`blindOrDisabled-${item.personId}`}
                                type="checkbox"
                            >
                                <GICheckbox
                                    id={`blindOrDisabled-${item.personId}`}
                                    checked={item.blindOrDisabled}
                                    value={item.blindOrDisabled}
                                    onChange={e => onHandleHasDisabilityChange(item.personId, !item.blindOrDisabled)}
                                />
                                <GIFormLabel
                                    id={`blindOrDisabledLabel-${item.personId}`}
                                    name={`blindOrDisabled-${item.personId}`}
                                    aria-label={`${item.name} ${intl.formatMessage({ id: 'indportal.tobacco.table.hasDisability.heading' })}`}
                                />
                            </GIFormControl>
                        );
                    },
                },
            ]
            : []),
        ...(isHardExemptionChecked
            ? [
                {
                    titleId: 'indportal.tobacco.table.hardshipExemptNum.heading',
                    key: 'hardshipExemptNum',
                    render: item => (
                        <GIFormControl id={`hardshipExemptionWrapper-${item.personId}`} type="text">
                            <label htmlFor={`hardshipExemption-${item.personId}`} className='sr-only'>{`${item.name} ${intl.formatMessage({ id: 'indportal.tobacco.table.hardshipExemptNum.heading' })}`}</label>
                            <GIInput
                                id={`hardshipExemption-${item.personId}`}
                                value={item.exemptionNumber || ''}
                                maxLength={VALIDATION_TYPES.HARDSHIP_EXEMPT_NUMBER_MAX_LENGTH}
                                size={InputSize.MD}
                                onChange={e =>
                                    onHandleExemptionNumberChange(
                                        item.personId,
                                        e.target.value.replace(DIGIT_ONLY_REGEX, ''),
                                    )
                                }
                            />
                        </GIFormControl>
                    ),
                },
            ]
            : []),
    ];

    return (
        <StyledWrapper>
            <Table columns={colHeader} data={data} />
        </StyledWrapper>
    );
};

export default HardshipExemptionEligibilityTable;
